import { useState, type FormEvent } from 'react';
import type { EmployeeProfileResponse, EmployeeTraitsResponse } from '../../api/types';
import { apiPost, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { FactSection } from './FactSection';
import { formatTimestamp } from './attributeDisplay';
import listStyles from './FactRecords.module.css';
import factStyles from './CaptureSection.module.css';
import formStyles from './AssessmentForm.module.css';

const DERIVATION_LABEL: Record<'manual' | 'system', string> = {
  manual: 'Manually assessed',
  system: 'System-derived',
};

/**
 * The Trait section (`docs/DOMAIN_MODEL.md` §2.11; Constitution §6) —
 * built Mission 166, implementing the specification
 * `docs/DOMAIN_DECISIONS.md`'s "Composite Concepts" (Mission 165)
 * authorised. Migration 0018 shipped the schema in 2026; no service, API
 * or UI ever existed against it before this mission.
 *
 * ---------------------------------------------------------------------
 * A TRAIT IS NOT AN ATTRIBUTE, A SKILL, A CAPABILITY, OR A TALENT
 * DESIGNATION
 *
 * This section sits beneath Skills/Experience/Qualifications/Achievements,
 * which makes the boundary easy to lose by accident (Mission 165 Part 1's
 * boundary matrix, Part 5's pairwise distinctions). Held apart in the
 * copy itself, not only in code:
 *
 * - **No rating, ever.** A Trait is a label a person observed or assessed,
 *   never a 1-20 number — unlike Attribute and Capability, which are.
 *   This section renders no scale, no bar, no numeric badge of any kind.
 * - **No confidence.** The schema has no confidence column for
 *   `employee_trait`; nothing here invents one.
 * - **Provenance is stated plainly, in words** ("Manually assessed" /
 *   "System-derived"), never left as raw enum text — the same
 *   `docs/DOMAIN_DECISIONS.md`-established convention every other
 *   provenance/status field in this application already follows.
 *   "System-derived" never appears from this UI's own writes: there is no
 *   derivation engine (Mission 165 Part 3D/3E), so every record this
 *   section can create is manually assessed.
 * - **Not a Talent Designation.** A Trait describes *how someone tends to
 *   behave* (broadly stable); a Talent Designation describes *where
 *   someone stands in the organisation's talent conversation right now*
 *   (time-boxed, expected to change). This section has no review date, no
 *   expiry, no withdrawal — those belong to Talent Designation, not here.
 *
 * WRITE PATH: RECORD AND CORRECT ONLY, NO WITHDRAWAL
 *
 * Mission 165/166 authorised create and correct — a reassessment is a new
 * row (append-only, `employee_trait_forbid_mutation`), never an in-place
 * edit. There is no "Withdraw" control: no distinct terminal state was
 * authorised for Trait (unlike Talent Designation, which has
 * `active | expired | withdrawn`).
 *
 * Rationale is REQUIRED here (unlike most optional-rationale writes in
 * this application) — a human asserting a behavioural pattern about
 * someone else without saying why is exactly the kind of unaccountable
 * judgement Constitution §3.3 warns against.
 *
 * `canWrite` reuses `viewerCanAssessAs !== null` directly — the same
 * signal Employee Facts and Skill Evidence already use, since recording a
 * trait observation is the same kind of act (Mission 165 Part 3C).
 * ---------------------------------------------------------------------
 */
export function TraitSection({ profile }: { profile: EmployeeProfileResponse }) {
  const canWrite = profile.viewerCanAssessAs !== null;
  const traits = useApiResource<EmployeeTraitsResponse>(`/employees/${profile.id}/traits`);
  const [recording, setRecording] = useState(false);

  return (
    <>
      <FactSection
        id="traits"
        title="Traits"
        state={traits}
        loadingLabel="Loading traits…"
        errorTitle="Traits could not be loaded"
        isEmpty={(data) => data.traits.length === 0}
        empty={{
          title: 'No traits recorded',
          detail: canWrite
            ? 'Nothing has been recorded yet. Use "Record trait" below to add one.'
            : 'Nothing has been recorded here yet. That is an absence of a record, not a finding about this person.',
        }}
        note="A broader behavioural pattern someone observed or assessed — never a rating, and never inferred from attributes, skills or achievements."
      >
        {(data) => (
          <ul className={listStyles.list}>
            {data.traits.map((trait) => (
              <TraitRecordCard key={trait.id} employeeId={data.employeeId} trait={trait} canWrite={canWrite} onChanged={() => traits.reload()} />
            ))}
          </ul>
        )}
      </FactSection>

      {canWrite && traits.status === 'loaded' ? (
        recording ? (
          <TraitForm
            employeeId={profile.id}
            onRecorded={() => {
              setRecording(false);
              traits.reload();
            }}
            onClose={() => setRecording(false)}
          />
        ) : (
          <button type="button" className={factStyles.addToggle} onClick={() => setRecording(true)}>
            Record trait
          </button>
        )
      ) : null}
    </>
  );
}

function TraitRecordCard({
  employeeId,
  trait,
  canWrite,
  onChanged,
}: {
  employeeId: string;
  trait: EmployeeTraitsResponse['traits'][number];
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [correcting, setCorrecting] = useState(false);

  return (
    <li className={listStyles.record}>
      <div className={listStyles.primary}>
        <span className={listStyles.name}>{trait.label}</span>
        <span className={listStyles.marker}>{DERIVATION_LABEL[trait.derivation]}</span>
        {canWrite ? (
          <button type="button" className={factStyles.editToggle} onClick={() => setCorrecting((open) => !open)}>
            {correcting ? 'Cancel' : 'Correct'}
          </button>
        ) : null}
      </div>

      <dl className={listStyles.facts}>
        <div>
          <dt>Recorded</dt>
          <dd>{formatTimestamp(trait.assessedAt)}</dd>
        </div>
        {trait.assessor ? (
          <div>
            <dt>Assessed by</dt>
            <dd>{trait.assessor.fullName}</dd>
          </div>
        ) : null}
      </dl>

      {trait.rationale ? <p className={listStyles.notes}>{trait.rationale}</p> : null}

      {correcting ? (
        <TraitForm
          employeeId={employeeId}
          correcting={{ traitId: trait.id, label: trait.label, currentRationale: trait.rationale }}
          onRecorded={() => {
            setCorrecting(false);
            onChanged();
          }}
          onClose={() => setCorrecting(false)}
        />
      ) : null}
    </li>
  );
}

type SubmitState = { status: 'editing' } | { status: 'submitting' } | { status: 'failed'; message: string };

function TraitForm({
  employeeId,
  correcting,
  onRecorded,
  onClose,
}: {
  employeeId: string;
  correcting?: { traitId: string; label: string; currentRationale: string | null };
  onRecorded: () => void;
  onClose: () => void;
}) {
  const [label, setLabel] = useState(correcting?.label ?? '');
  const [rationale, setRationale] = useState(correcting?.currentRationale ?? '');
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';
  const rationaleIsValid = rationale.trim().length > 0;
  const labelIsValid = label.trim().length > 0;
  const fieldId = (name: string) => `trait-${name}${correcting ? `-${correcting.traitId}` : '-new'}`;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting || !rationaleIsValid || (!correcting && !labelIsValid)) return;
    setState({ status: 'submitting' });
    try {
      const path = correcting ? `/employees/${employeeId}/traits/${correcting.traitId}/correction` : `/employees/${employeeId}/traits`;
      await apiPost(path, {
        ...(correcting ? {} : { label: label.trim() }),
        rationale: rationale.trim(),
      });
      onRecorded();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form className={formStyles.form} onSubmit={handleSubmit} aria-label={correcting ? 'Correct trait' : 'Record trait'}>
      {correcting ? (
        <p className={formStyles.perspective}>
          This records a <strong>replacement</strong> for the current "{correcting.label}" observation. The earlier
          record is kept and stays on record; it stops counting as current.
        </p>
      ) : (
        <div className={formStyles.field}>
          <label htmlFor={fieldId('label')} className={formStyles.label}>
            Trait
          </label>
          <input
            id={fieldId('label')}
            className={formStyles.text}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            disabled={submitting}
            placeholder="e.g. Natural Mentor"
            maxLength={200}
          />
        </div>
      )}

      <div className={formStyles.field}>
        <label htmlFor={fieldId('rationale')} className={formStyles.label}>
          Rationale
        </label>
        <textarea
          id={fieldId('rationale')}
          className={formStyles.textarea}
          rows={2}
          value={rationale}
          onChange={(event) => setRationale(event.target.value)}
          disabled={submitting}
          placeholder="What you observed, and why this trait applies"
          maxLength={2000}
          required
        />
      </div>

      {state.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting || !rationaleIsValid || (!correcting && !labelIsValid)}>
          {submitting ? 'Saving…' : correcting ? 'Record correction' : 'Record trait'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}
